import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import { Activity, Cpu, MapPin, ShieldCheck, Zap } from "lucide-react";
import BharatMap from "./BharatMap";

const growthData = [
  { y: "11", v: 28 },
  { y: "13", v: 36 },
  { y: "15", v: 41 },
  { y: "17", v: 55 },
  { y: "19", v: 64 },
  { y: "21", v: 76 },
  { y: "23", v: 89 },
  { y: "25", v: 100 },
  { y: "26", v: 112 },
];

const sectorAlloc = [
  { name: "Health", v: 24 },
  { name: "Edu", v: 19 },
  { name: "Infra", v: 28 },
  { name: "Workforce", v: 14 },
  { name: "Digital", v: 22 },
];

const readiness = [{ name: "DPI", value: 78, fill: "#C5A880" }];

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 bg-navy-700 border border-gold/30 text-xs text-ivory">
      <p className="text-gold">{label}</p>
      <p>{payload[0].value}</p>
    </div>
  );
};

export const DigitalBharatCommand = () => {
  return (
    <section
      id="command-center"
      data-testid="command-center-section"
      className="relative bg-navy text-ivory py-28 md:py-40 scroll-mt-nav overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-grid-faint opacity-25"
        style={{ backgroundSize: "60px 60px" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy to-navy-700/60 pointer-events-none" />

      <div className="relative container-bharat">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="overline text-gold">Digital Bharat · Command Center</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.02] tracking-tight">
              The data layer of a{" "}
              <span className="italic text-gold">developed Bharat</span>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-ivory/65 leading-relaxed">
            A glimpse into the analytics fabric we build for ministries —
            integrating governance, infrastructure and citizen platforms into a
            single intelligence layer.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-ivory/10 border border-ivory/10">
          {/* Big map */}
          <div className="lg:col-span-7 bg-navy-800 p-8 lg:p-10 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 overline text-gold">
                <MapPin size={14} /> Pan-India Engagements
              </div>
              <span className="text-xs text-ivory/50 font-display tracking-[0.3em]">
                LIVE
                <span className="inline-block ml-2 w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" />
              </span>
            </div>
            <BharatMap />
            <div className="mt-6 grid grid-cols-3 gap-px bg-ivory/10">
              {[
                { k: "States", v: "18" },
                { k: "Programs", v: "62" },
                { k: "Districts", v: "214" },
              ].map((m) => (
                <div key={m.k} className="bg-navy-800 p-4">
                  <p className="font-serif-display text-2xl">{m.v}</p>
                  <p className="text-[0.7rem] text-ivory/50 mt-1">
                    {m.k}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right column charts */}
          <div className="lg:col-span-5 grid grid-rows-3 gap-px">
            <div className="bg-navy-800 p-8">
              <div className="flex items-center justify-between">
                <div className="overline text-gold flex items-center gap-2">
                  <Activity size={14} /> Engagement Index
                </div>
                <span className="text-xs text-emerald">+13.4%</span>
              </div>
              <p className="font-serif-display text-3xl mt-3">112 / 120</p>
              <div className="h-20 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growthData}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C5A880" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#C5A880" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="y" hide />
                    <YAxis hide />
                    <Tooltip content={<Tip />} cursor={{ stroke: "#C5A880", strokeOpacity: 0.3 }} />
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke="#C5A880"
                      strokeWidth={1.5}
                      fill="url(#g1)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-navy-800 p-8">
              <div className="overline text-gold flex items-center gap-2">
                <Cpu size={14} /> Sector Allocation
              </div>
              <div className="h-28 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sectorAlloc} barCategoryGap={6}>
                    <XAxis dataKey="name" tick={{ fill: "#A0AAB2", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip content={<Tip />} cursor={{ fill: "rgba(197,168,128,0.1)" }} />
                    <Bar dataKey="v" fill="#C5A880" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-navy-800 p-8 flex items-center gap-6">
              <div className="w-28 h-28 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart
                    innerRadius="70%"
                    outerRadius="100%"
                    data={readiness}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <PolarAngleAxis
                      type="number"
                      domain={[0, 100]}
                      tick={false}
                    />
                    <RadialBar background={{ fill: "rgba(255,255,255,0.06)" }} dataKey="value" cornerRadius={2} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center font-serif-display text-2xl">
                  78%
                </div>
              </div>
              <div>
                <div className="overline text-gold flex items-center gap-2">
                  <ShieldCheck size={14} /> DPI Readiness
                </div>
                <p className="mt-3 text-sm text-ivory/70 leading-relaxed max-w-xs">
                  Composite index across identity, payments, data and
                  governance rails.
                </p>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-10 flex items-center gap-3 text-xs text-ivory/50"
        >
          <Zap size={14} className="text-gold" />
          Illustrative dashboard · Engineered with our analytics partners.
        </motion.div>
      </div>
    </section>
  );
};

export default DigitalBharatCommand;
